import React, { useState, useRef, useEffect } from 'react';
import '../styles/GalleryPage.css';

// Interface for generated shape
export interface GeneratedShape {
  id: string;
  elementType: string;
  pathData: string;
  points: number;
  createdAt: string;
  color?: string; // Optional color property
}

// Interface for processed shape with Date object
interface ProcessedShape {
  id: string;
  elementType: string;
  pathData: string;
  points: number;
  createdAt: Date;
  color: string; // Color property (default to red if not provided)
}

interface GalleryPageProps {
  shapes: GeneratedShape[];
  onBack: () => void;
  onClearAll?: () => void;
  onImport?: (shapes: GeneratedShape[]) => void;
}

const GalleryPage: React.FC<GalleryPageProps> = ({ shapes, onBack, onClearAll, onImport }) => {
  // Convert ISO date strings to Date objects
  const processedShapes: ProcessedShape[] = shapes.map(shape => ({
    ...shape,
    elementType: shape.elementType.replace("Grandmother's ", ""),
    createdAt: new Date(shape.createdAt),
    color: shape.color || '#FF0000' // Default to red if color not provided
  }));

  // Sort shapes by creation date (newest first)
  const sortedShapes = [...processedShapes].sort((a, b) => 
    b.createdAt.getTime() - a.createdAt.getTime()
  );
  
  // Use "all" as the default tab
  const [activeTab, setActiveTab] = useState('all');
  const [enlargedShape, setEnlargedShape] = useState<ProcessedShape | null>(null);
  
  // Create a reference for the hidden file input
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Predefined element types (tabs we want to show)
  const predefinedElementTypes = ['House', 'River', 'Road', 'Basket', 'Bridge', 'Tree'];
  
  // Group shapes by element type
  const shapesByElement = sortedShapes.reduce<Record<string, ProcessedShape[]>>((acc, shape) => {
    // The element type is already normalized in processedShapes
    if (!acc[shape.elementType]) {
      acc[shape.elementType] = [];
    }
    acc[shape.elementType].push(shape);
    return acc;
  }, {});
  
  // Ensure all predefined element types exist in the map, even if empty
  predefinedElementTypes.forEach(type => {
    if (!shapesByElement[type]) {
      shapesByElement[type] = [];
    }
  });
  
  // Use predefined order for element types
  const elementTypes = predefinedElementTypes;
  
  // Function to export all shapes as JSON
  const handleExportAll = () => {
    try {
      const dataStr = JSON.stringify(shapes, null, 2);
      const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
      
      const exportName = `shapes-backup-${new Date().toISOString().slice(0, 10)}.json`;
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportName);
      document.body.appendChild(linkElement);
      linkElement.click();
      document.body.removeChild(linkElement);
    } catch (error) {
      console.error('Error exporting shapes:', error);
      alert('There was an issue exporting your shapes. Please try again.');
    }
  };
  
  // Function to handle file selection for import
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const importedShapes = JSON.parse(content) as GeneratedShape[];
        
        // Validate the imported shapes
        if (!Array.isArray(importedShapes)) {
          throw new Error('Invalid format: Expected an array of shapes');
        }
        
        // Check if each shape has the required fields
        const isValid = importedShapes.every(shape => 
          shape.id && shape.elementType && shape.pathData && 
          shape.points && shape.createdAt
        );
        
        if (!isValid) {
          throw new Error('Invalid shape data: Some shapes are missing required fields');
        }
        
        // Call the import callback if provided
        if (onImport) {
          onImport(importedShapes);
        } else {
          alert('Import functionality is not available at this time.');
        }
      } catch (error) {
        console.error('Error importing shapes:', error);
        alert(`Error importing shapes: ${(error as Error).message}`);
      }
      
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
    
    reader.readAsText(file);
  };
  
  // Function to handle clicking on a shape to enlarge it
  const handleShapeClick = (shape: ProcessedShape) => {
    setEnlargedShape(shape);
  };
  
  // Function to close the enlarged shape view
  const handleCloseEnlarged = () => {
    setEnlargedShape(null);
  };
  
  // Render a specific shape item with metaball styling
  const ShapeItem: React.FC<{shape: ProcessedShape, onClick?: () => void}> = ({ shape, onClick }) => {
    // Add useRef and useState for path bounds
    const svgRef = useRef<SVGSVGElement>(null);
    const [shapeBounds, setShapeBounds] = useState<{
      minX: number, minY: number, width: number, height: number
    } | null>(null);
    
    // Use black border for all shapes
    const borderColor = '#000';
    const fillColor = shape.elementType.toLowerCase().includes('tree') ? '#4CAF50' : shape.color;
    
    // Calculate SVG bounds for proper rendering
    useEffect(() => {
      // Create a temporary SVG to measure the path
      const tempSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      tempSvg.style.position = 'absolute';
      tempSvg.style.visibility = 'hidden';
      document.body.appendChild(tempSvg);
      
      const tempPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      tempPath.setAttribute('d', shape.pathData);
      tempSvg.appendChild(tempPath);
      
      // Get the bounding box
      const bbox = tempPath.getBBox();
      
      // Clean up
      document.body.removeChild(tempSvg);
      
      // Add padding (20% on each side)
      const padding = Math.max(bbox.width, bbox.height) * 0.2;
      
      setShapeBounds({
        minX: bbox.x - padding,
        minY: bbox.y - padding,
        width: bbox.width + padding * 2,
        height: bbox.height + padding * 2
      });
    }, [shape.pathData]);
    
    // Generate proper viewBox
    const viewBox = shapeBounds 
      ? `${shapeBounds.minX} ${shapeBounds.minY} ${shapeBounds.width} ${shapeBounds.height}`
      : "0 0 600 600"; // Fallback
    
    return (
      <div className="gallery-item" onClick={onClick} style={{ borderColor }}>
        <div className="shape-container metaball-shape">
          <svg ref={svgRef} width="200" height="200" viewBox={viewBox}>
            <path
              d={shape.pathData}
              fill={fillColor}
              stroke={fillColor}
              strokeWidth="15"
              fillRule="evenodd"
              className="metaball-path"
            />
          </svg>
        </div>
      </div>
    );
  };
  
  // Function to export SVG
  const exportAsSVG = (pathData: string, color: string, elementType: string) => {
    const viewBox = getShapeBounds(pathData);
    
    // Normalize the element type
    const normalizedType = elementType.replace("Grandmother's ", "");
    
    // Adjust color for trees
    const shapeColor = normalizedType.toLowerCase().includes('tree') ? '#4CAF50' : color;
    
    const svgContent = `
      <svg xmlns="http://www.w3.org/2000/svg" width="600" height="600" viewBox="${viewBox}">
        <path d="${pathData}" fill="${shapeColor}" stroke="${shapeColor}" stroke-width="1" stroke-linejoin="round" stroke-linecap="round" 
          vector-effect="non-scaling-stroke" shape-rendering="geometricPrecision" />
      </svg>
    `;
    
    const blob = new Blob([svgContent], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${normalizedType.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };
  
  // Add a utility function to compute path bounds for any path
  const getShapeBounds = (pathData: string): string => {
    try {
      // Create a temporary SVG to measure the path
      const tempSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      tempSvg.style.position = 'absolute';
      tempSvg.style.visibility = 'hidden';
      document.body.appendChild(tempSvg);
      
      const tempPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      tempPath.setAttribute('d', pathData);
      tempSvg.appendChild(tempPath);
      
      // Get the bounding box
      const bbox = tempPath.getBBox();
      
      // Clean up
      document.body.removeChild(tempSvg);
      
      // Add padding (20% on each side)
      const padding = Math.max(bbox.width, bbox.height) * 0.2;
      
      return `${bbox.x - padding} ${bbox.y - padding} ${bbox.width + padding * 2} ${bbox.height + padding * 2}`;
    } catch (e) {
      console.error("Error calculating path bounds:", e);
      return "0 0 600 600"; // Fallback
    }
  };
  
  return (
    <div className="gallery-page">
      <div className="gallery-header">
        <h1>Generated Gallery</h1>
        <div className="gallery-header-buttons">
          <button className="back-button" onClick={onBack}>
            Back to Drawing
          </button>
          <button className="export-button" onClick={handleExportAll}>
            Export All
          </button>
          {onClearAll && shapes.length > 0 && (
            <button className="clear-button" onClick={onClearAll}>
              Clear All Shapes
            </button>
          )}
        </div>
      </div>
      
      {/* Hidden file input for importing shapes */}
      <input 
        type="file" 
        ref={fileInputRef} 
        style={{ display: 'none' }} 
        accept=".json"
        onChange={handleFileSelect}
      />
      
      <div className="gallery-content">
        <div className="gallery-tabs">
          <div 
            className={`gallery-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All
          </div>
          {elementTypes.map(element => (
            <div 
              key={element}
              className={`gallery-tab ${activeTab === element ? 'active' : ''}`}
              onClick={() => setActiveTab(element)}
            >
              {element}
            </div>
          ))}
        </div>
        
        <div className="gallery-tab-content">
          {sortedShapes.length === 0 ? (
            <div className="gallery-empty">
              <p>No shapes have been generated yet.</p>
              <div className="gallery-empty-buttons">
                <button className="back-button" onClick={onBack}>
                  Create Your First Shape
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* All Shapes Tab */}
              <div className={`tab-content ${activeTab === 'all' ? 'active' : ''}`}>
                <div className="gallery-container">
                  <div className="gallery-grid">
                    {sortedShapes.map((shape) => (
                      <ShapeItem key={shape.id} shape={shape} onClick={() => handleShapeClick(shape)} />
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Element-specific Views */}
              {elementTypes.map(element => (
                <div 
                  key={element}
                  className={`tab-content ${activeTab === element ? 'active' : ''}`}
                >
                  <div className="gallery-container">
                    <div className="gallery-grid">
                      {shapesByElement[element]?.map((shape) => (
                        <ShapeItem key={shape.id} shape={shape} onClick={() => handleShapeClick(shape)} />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
      
      {enlargedShape && (
        <div className="enlarged-shape-overlay" onClick={handleCloseEnlarged}>
          <div className="enlarged-shape-container" onClick={(e) => e.stopPropagation()}>
            <h2>{enlargedShape.elementType}</h2>
            <div className="enlarged-shape">
              <svg width="400" height="400" viewBox={getShapeBounds(enlargedShape.pathData)}>
                <path
                  d={enlargedShape.pathData}
                  fill={enlargedShape.elementType.toLowerCase().includes('tree') ? '#4CAF50' : enlargedShape.color}
                  stroke={enlargedShape.elementType.toLowerCase().includes('tree') ? '#4CAF50' : enlargedShape.color}
                  strokeWidth="15"
                  fillRule="evenodd"
                  className="metaball-path"
                />
              </svg>
            </div>
            <div className="shape-details">
              <p>Created: {enlargedShape.createdAt.toLocaleString()}</p>
              <button 
                className="save-button"
                onClick={() => exportAsSVG(enlargedShape.pathData, enlargedShape.color, enlargedShape.elementType)}
              >
                Save as SVG
              </button>
            </div>
            <button className="close-button" onClick={handleCloseEnlarged}>×</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage; 